// UTM preservation across sessions.
//
// GA4 auto-captures `utm_source` / `utm_medium` / `utm_campaign` / `utm_term`
// / `utm_content` for the session the user arrives in. But our sign-up flow
// spans more than one session: user lands -> requests magic link -> opens
// inbox (no UTM there) -> clicks link -> account is created. By the time
// `sign_up` fires the UTMs are gone unless we keep them.
//
// This module:
//   1. captureUtmFromUrl()  — reads the current URL on each page load and
//      stores any utm_* it finds in localStorage. Last UTM wins.
//   2. getStoredUtm()       — reads what's saved so events like `sign_up`
//      can pin themselves to the original source.
//
// We don't expire the value — once the user is identified the link is made
// and there's no point dropping it.

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  // Ad click IDs: gclid drives Google Ads offline conversion uploads,
  // gbraid/wbraid are its consent-mode/iOS variants, rdt_cid is the Reddit
  // equivalent (Reddit Conversions API). Captured together with UTMs and
  // persisted server-side at signup (users.signup_attribution).
  "gclid",
  "gbraid",
  "wbraid",
  "rdt_cid",
] as const;

const STORAGE_KEY = "upwex_utm";

export function captureUtmFromUrl(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  let hasAny = false;
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) {
      utm[k] = v;
      hasAny = true;
    }
  }
  if (hasAny) {
    try {
      utm.captured_at = String(Date.now());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    } catch {
      // localStorage can throw in private mode — silently ignore.
    }
  }
}

export function getStoredUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    const { captured_at: _ignored, ...rest } = parsed;
    return rest;
  } catch {
    return {};
  }
}
